import axios, { AxiosError } from "axios";
import { AffiliateAccount, TierCode, TierConfiguration } from "../../util/api/affiliateApi";

import {
  AffiliateDiscountCode,
  BugReportDetail,
  BugReportExportOption,
  BugReportSummary,
  DashboardSummary,
  DiscountCodeResult,
  ProductCatalogItem,
  ProductKeyResult,
  QueryResult,
  SupportReply,
  SupportSession,
  TicketDetail,
  TicketState,
  TicketSummary,
  OwnedLicense,
  LicenseDetail,
  ActivationLookupItem,
  UserLicenses,
  UserHistoryEvent,
  UserPasswordResetResult,
  PromoLink,
  PromoLinkContent,
  CreatePromoLinkRequest,
  VatExportOptions,
  VatExportRequest,
  VatExportJob,
} from "./types";


export const supportApi = axios.create({
  baseURL: "/api/support-admin",
  withCredentials: true,
  headers: { "Cache-Control": "no-store" },
});

let invalidSessionHandler: (() => void) | null = null;

export function setInvalidSessionHandler(handler: (() => void) | null) {
  invalidSessionHandler = handler;
}

supportApi.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const detail = (error.response?.data as any)?.detail;
    if (status === 401 || (status === 403 && detail?.code === "admin_disabled")) {
      invalidSessionHandler?.();
    }
    return Promise.reject(error);
  }
);

function csrfHeaders(csrfToken: string) {
  return { "X-Support-CSRF": csrfToken };
}

function bugReportExportFilename(productId: string, productVersion: string): string {
  const clean = (value: string) =>
    value.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "unknown";
  return `wavemind-bug-reports-${clean(productId)}-${clean(productVersion)}.json`;
}

function saveBugReportExport(blob: Blob, productId: string, productVersion: string): void {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const cleanup = () => {
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  try {
    link.href = objectUrl;
    link.download = bugReportExportFilename(productId, productVersion);
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
  } finally {
    window.setTimeout(cleanup, 200);
  }
}

export function errorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return "Something went wrong.";
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  return "The support service could not complete that request.";
}

export const api = {
  vatExportOptions: async () =>
    (await supportApi.get<VatExportOptions>("/vat-export/options")).data,
  emailVatExport: async (request: VatExportRequest, csrfToken: string) =>
    (await supportApi.post<VatExportJob>("/vat-export/email", request, {
      headers: csrfHeaders(csrfToken),
    })).data,
  getVatExportJobs: async () =>
    (await supportApi.get<{ jobs: VatExportJob[] }>("/vat-export/jobs")).data,
  uploadPromoImage: async (file: File, csrfToken: string) => {
    const form = new FormData();
    form.append("file", file);
    return (await supportApi.post<{ image_url: string; width: number; height: number; size_bytes: number }>(
      "/promo-links/images", form, { headers: csrfHeaders(csrfToken) }
    )).data;
  },
  promoProducts: async () =>
    (await supportApi.get<ProductCatalogItem[]>("/promo-links/products")).data,
  promoLinks: async (csrfToken: string) =>
    (await supportApi.post<PromoLink[]>("/promo-links/query", {}, { headers: csrfHeaders(csrfToken) })).data,
  createPromoLink: async (request: CreatePromoLinkRequest, csrfToken: string) =>
    (await supportApi.post<PromoLink>("/promo-links", request, { headers: csrfHeaders(csrfToken) })).data,
  updatePromoLinkContent: async (id: string, content: PromoLinkContent, csrfToken: string) =>
    (await supportApi.post<PromoLink>(`/promo-links/${id}/content`, content, { headers: csrfHeaders(csrfToken) })).data,
  disablePromoLink: async (id: string, idempotencyKey: string, csrfToken: string) =>
    (await supportApi.post<PromoLink>(`/promo-links/${id}/disable`, { idempotency_key: idempotencyKey }, { headers: csrfHeaders(csrfToken) })).data,
  resumePromoLink: async (id: string, csrfToken: string) =>
    (await supportApi.post<PromoLink>(`/promo-links/${id}/retry`, {}, { headers: csrfHeaders(csrfToken) })).data,
  affiliateDiscountCodes: async (id: string, csrf: string) =>
    (await supportApi.post<AffiliateDiscountCode[]>(`/affiliates/${id}/discount-codes/query`, {}, { headers: csrfHeaders(csrf) })).data,
  assignAffiliateDiscount: async (id: string, code: string, percentage: string, csrf: string) =>
    (await supportApi.post<AffiliateDiscountCode>(`/affiliates/${id}/discount-codes`, { code, percentage }, { headers: csrfHeaders(csrf) })).data,
  queryTiers: async (csrf: string) => (await supportApi.post<TierConfiguration>("/affiliates/tiers/query", {}, { headers: csrfHeaders(csrf) })).data,
  configureTiers: async (data: Omit<TierConfiguration, "enabled">, csrf: string) => (await supportApi.post<TierConfiguration>("/affiliates/tiers/configure", data, { headers: csrfHeaders(csrf) })).data,
  assignTier: async (id: string, tier_code: TierCode | null, csrf: string) => (await supportApi.post<AffiliateAccount>(`/affiliates/${id}/tier`, { tier_code }, { headers: csrfHeaders(csrf) })).data,
  queryAffiliates: async (query: { search: string; approval_state: string | null; verified: boolean | null; page: number; page_size: number }, csrfToken: string) =>
    (await supportApi.post<QueryResult<AffiliateAccount>>("/affiliates/query", query, { headers: csrfHeaders(csrfToken) })).data,
  affiliateDetail: async (id: string, csrfToken: string) =>
    (await supportApi.post<AffiliateAccount>(`/affiliates/${id}/detail`, {}, { headers: csrfHeaders(csrfToken) })).data,
  setAffiliateStatus: async (id: string, approval_state: "active" | "suspended", csrfToken: string) =>
    (await supportApi.post<AffiliateAccount>(`/affiliates/${id}/status`, { approval_state }, { headers: csrfHeaders(csrfToken) })).data,
  setAffiliatePercentage: async (id: string, commission_percentage: string, csrfToken: string) =>
    (await supportApi.post<AffiliateAccount>(`/affiliates/${id}/percentage`, { commission_percentage }, { headers: csrfHeaders(csrfToken) })).data,
  lookupActivations: async (machineName: string, page: number, csrfToken: string) =>
    (await supportApi.post<QueryResult<ActivationLookupItem>>(
      "/activations/lookup", { machine_name: machineName, page, page_size: 25 }, { headers: csrfHeaders(csrfToken) }
    )).data,
  userHistory: async (userId: string, page: number, csrfToken: string) =>
    (await supportApi.post<QueryResult<UserHistoryEvent>>(
      `/users/${userId}/history`, { page, page_size: 25 }, { headers: csrfHeaders(csrfToken) }
    )).data,
  generateUserPasswordResetLink: async (userId: string, csrfToken: string, validityMinutes = 120) =>
    (await supportApi.post<UserPasswordResetResult>(
      `/users/${userId}/password-reset-link`, { validity_minutes: validityMinutes }, { headers: csrfHeaders(csrfToken) }
    )).data,
  lookupLicense: async (licenseKey: string, csrfToken: string) =>
    (await supportApi.post<LicenseDetail>(
      "/licenses/lookup", { license_key: licenseKey }, { headers: csrfHeaders(csrfToken) }
    )).data,
  disableLicense: async (licenseId: string, csrfToken: string) =>
    (await supportApi.post<OwnedLicense>(
      `/licenses/${licenseId}/disable`, {}, { headers: csrfHeaders(csrfToken) }
    )).data,
  deleteLicenseActivation: async (licenseId: string, activationId: string, csrfToken: string) =>
    (await supportApi.delete(
      `/licenses/${licenseId}/activations/${activationId}`, { headers: csrfHeaders(csrfToken) }
    )).data,
  lookupUserLicenses: async (email: string, csrfToken: string) =>
    (await supportApi.post<{ users: UserLicenses[] }>(
      "/users/lookup", { email }, { headers: csrfHeaders(csrfToken) }
    )).data,
  disableUserLicense: async (userId: string, licenseId: string, csrfToken: string) =>
    (await supportApi.post<OwnedLicense>(
      `/users/${userId}/licenses/${licenseId}/disable`, {}, { headers: csrfHeaders(csrfToken) }
    )).data,
  current: async () => (await supportApi.get<SupportSession>("/auth/current")).data,
  login: async (email: string, password: string) =>
    (await supportApi.post<SupportSession>("/auth/login", { email, password })).data,
  logout: async (csrfToken: string) =>
    (await supportApi.post("/auth/logout", {}, { headers: csrfHeaders(csrfToken) })).data,
  reauthenticate: async (password: string, csrfToken: string) =>
    (
      await supportApi.post<SupportSession>(
        "/auth/reauthenticate",
        { password },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  summary: async () => (await supportApi.get<DashboardSummary>("/dashboard/summary")).data,
  queryTickets: async (
    csrfToken: string,
    state: TicketState,
    search: string,
    page: number,
    pageSize: number
  ) =>
    (
      await supportApi.post<QueryResult<TicketSummary>>(
        "/tickets/query",
        { state, search, page, page_size: pageSize },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  queryBugReports: async (
    csrfToken: string,
    state: TicketState,
    search: string,
    page: number,
    pageSize: number
  ) =>
    (
      await supportApi.post<QueryResult<BugReportSummary>>(
        "/bug-reports/query",
        { state, search, page, page_size: pageSize },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  bugReportExportOptions: async () =>
    (await supportApi.get<BugReportExportOption[]>("/bug-reports/export-options")).data,
  exportBugReports: async (
    productId: string,
    productVersion: string,
    csrfToken: string
  ): Promise<void> => {
    const response = await supportApi.post<Blob>(
      "/bug-reports/export",
      { product_id: productId, product_version: productVersion },
      { headers: csrfHeaders(csrfToken), responseType: "blob" }
    );
    saveBugReportExport(response.data, productId, productVersion);
  },
  ticket: async (id: string) => (await supportApi.get<TicketDetail>(`/tickets/${id}`)).data,
  bugReport: async (id: string) =>
    (await supportApi.get<BugReportDetail>(`/bug-reports/${id}`)).data,
  setTicketStatus: async (
    id: string,
    status: "open" | "closed",
    expectedUpdatedAt: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<TicketDetail>(
        `/tickets/${id}/status`,
        { status, expected_updated_at: expectedUpdatedAt },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  setBugReportStatus: async (
    id: string,
    status: "open" | "closed",
    expectedUpdatedAt: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<BugReportDetail>(
        `/bug-reports/${id}/status`,
        { status, expected_updated_at: expectedUpdatedAt },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  sendReply: async (
    ticketId: string,
    responseText: string,
    closeAfterSend: boolean,
    idempotencyKey: string,
    csrfToken: string,
    previousReplyId?: string
  ) => {
    const path = previousReplyId
      ? `/tickets/${ticketId}/replies/retry`
      : `/tickets/${ticketId}/replies`;
    const body: any = {
      response_text: responseText,
      close_after_send: closeAfterSend,
      idempotency_key: idempotencyKey,
    };
    if (previousReplyId) body.previous_reply_id = previousReplyId;
    return (
      await supportApi.post<SupportReply>(path, body, { headers: csrfHeaders(csrfToken) })
    ).data;
  },
  sendBugReportReply: async (
    reportId: string,
    responseText: string,
    closeAfterSend: boolean,
    idempotencyKey: string,
    csrfToken: string,
    previousReplyId?: string
  ) => {
    const path = previousReplyId
      ? `/bug-reports/${reportId}/replies/retry`
      : `/bug-reports/${reportId}/replies`;
    const body: any = {
      response_text: responseText,
      close_after_send: closeAfterSend,
      idempotency_key: idempotencyKey,
    };
    if (previousReplyId) body.previous_reply_id = previousReplyId;
    return (
      await supportApi.post<SupportReply>(path, body, { headers: csrfHeaders(csrfToken) })
    ).data;
  },
  catalog: async () =>
    (await supportApi.get<ProductCatalogItem[]>("/products/catalog")).data,
  createProductKey: async (
    productId: string,
    maxLicenses: number,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<ProductKeyResult>(
        "/product-keys",
        {
          product_id: productId,
          max_licenses: maxLicenses,
          idempotency_key: idempotencyKey,
        },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  createDiscountCode: async (
    name: string,
    customCode: string | undefined,
    percentage: number,
    maxRedemptions: number,
    expiresAt: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        "/discount-codes",
        {
          name,
          custom_code: customCode,
          percentage,
          max_redemptions: maxRedemptions,
          expires_at: expiresAt,
          idempotency_key: idempotencyKey,
        },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  discountCodes: async (csrfToken: string) =>
    (
      await supportApi.get<DiscountCodeResult[]>("/discount-codes", {
        headers: csrfHeaders(csrfToken),
      })
    ).data,
  disableDiscountCode: async (
    promotionCodeId: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        `/discount-codes/${promotionCodeId}/disable`,
        { idempotency_key: idempotencyKey },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  deleteDiscountCode: async (
    promotionCodeId: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        `/discount-codes/${promotionCodeId}/delete`,
        { idempotency_key: idempotencyKey },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
};
